import { useIsAuthenticated } from '@azure/msal-react';
import { prepareArticleImage } from '@/utils/prepareArticleImage';
import { trackCustomEvent } from '@/lib/appInsights';
import { TrackingCustomEvents } from '@/constants/constants';
import { useUser } from '@/state/UserContext';
import { Link } from 'react-router-dom';
import LockIcon from '@/components/elements/LockIcon';
import ArticleTags from '@/components/ArticleTags';
import ArticleAuthor from '@/components/ArticleAuthor';

import {
  titleVariants,
  lockVariants,
  imageContainerVariants,
  contentContainerVariants,
  summaryVariants,
  containerVariants,
} from '@/components/ArticleCard.styles';

import type { VariantProps } from 'class-variance-authority';
import type { article } from '@/types/articles';

interface ArticleCardProps extends VariantProps<typeof containerVariants> {
  article: article;
  className?: string;
  summaryClassName?: string;
  background?: 'grey' | 'white';
}

function ArticleCard({
  article,
  className,
  summaryClassName,
  imagePosition = 'left',
  layout = 'default',
  background = 'grey',
}: ArticleCardProps) {
  const { email, userId } = useUser();
  const isAuthenticated = useIsAuthenticated();
  const { id, title, summary, publishedAt, categories, author, image, isPaid, hasAccess } = article;
  const articleUrl = `/article/${id}`;
  const imageUrl = prepareArticleImage(image, 'medium');

  const containerStyleClasses = containerVariants({ imagePosition, layout });
  const lockStyleClasses = lockVariants({ imagePosition, layout });
  const imageContainerStyleClasses = imageContainerVariants({ imagePosition, layout });
  const contentContainerStyleClasses = contentContainerVariants({ layout });
  const titleStyleClasses = titleVariants({ imagePosition, layout });
  const summaryStyleClasses = summaryVariants({ imagePosition });

  const handleLinkClick = () => {
    trackCustomEvent(TrackingCustomEvents.click.card.article, {
      article_id: id.toString(),
      article_pricing: isPaid ? 'paid' : 'free',
      author_id: author.lookupKey.toString(),
      user_auth_status: isAuthenticated ? 'logged_in' : 'anonymous',
      user_email: email ?? 'anonymous',
      user_id: userId ?? 'anonymous',
    });
  };

  return (
    <article
      data-cy={`${isPaid ? 'paid' : 'free'}-article-card`}
      className={`${containerStyleClasses} ${className ?? ''} ${background === 'grey' ? '!bg-[#fafafa]' : 'bg-white'}`}
    >
      {!hasAccess && <LockIcon color='white' position='left' className={lockStyleClasses} />}
      {layout !== 'noImage' && (
        <Link to={articleUrl} onClick={handleLinkClick} className={imageContainerStyleClasses}>
          {!hasAccess && (
            <div
              aria-hidden={true}
              className='absolute inset-0 z-10 bg-black bg-opacity-25 group-hover:bg-opacity-50 duration-300 ease-in-out'
            />
          )}
          <img
            alt={title}
            src={imageUrl}
            className='absolute w-full h-full object-cover group-hover:scale-[1.05] transition duration-300 ease-in-out'
          />
        </Link>
      )}

      <div className={contentContainerStyleClasses}>
        <Link
          to={articleUrl}
          onClick={handleLinkClick}
          data-cy='article-link'
          className='hover:underline hover:text-teal-600'
        >
          <h2 className={`${titleStyleClasses} ${isPaid ? '' : '!max-w-[unset]'}`} data-cy='article-card-title'>
            {title}
          </h2>
        </Link>

        {summary && <p className={`${summaryStyleClasses} ${summaryClassName ?? ''}`}>{summary}</p>}

        <footer className='md:pt-3 mt-auto'>
          <ArticleTags tags={categories} tagLimit={2} isPaid={isPaid} />
          <ArticleAuthor image={author.image} name={author.name} id={author.id} publishedAt={publishedAt} />
        </footer>
      </div>
    </article>
  );
}

export default ArticleCard;
